import { router } from '@inertiajs/react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { ConnectControllerButton } from '@/components/arcade/connect-controller-button';
import { useArcadeStorageBridge } from '@/hooks/use-arcade-storage-bridge';
import { useProControllers } from '@/hooks/use-pro-controllers';
import type { ArcadeControlCode } from '@/lib/arcade-controls';
import {
  proControllerToArcadeControls,
  proControllerToMenuGamepad,
} from '@/lib/pro-controller';

import {
  firstConnectedGamepads,
  readGamepads,
  standardGamepadToArcadeControls,
} from '@/lib/standard-gamepad';

// Virtual gamepad state from keyboard input (default cabinet wiring). Only used
// here to detect the "back to menu" control; the embed handles all gameplay
// input and per-event remapping itself.
interface VirtualGamepad {
  buttons: boolean[];
}

// Coin/back button lands on gamepad button index 7. P1 uses key '8', P2 '9'.
const BACK_KEYS_P1 = new Set(['8']);
const BACK_KEYS_P2 = new Set(['9']);

// Exit-modal keyboard wiring (parent receives the cabinet keys). The joystick
// emits WASD/arrows and the action buttons land on the menu keys below.
const EXIT_LEFT_KEYS = new Set(['a', 'arrowleft', 'w', 'arrowup']);
const EXIT_RIGHT_KEYS = new Set(['d', 'arrowright', 's', 'arrowdown']);
const EXIT_CONFIRM_KEYS = new Set([
  'enter',
  ' ',
  'u',
  'i',
  'o',
  'j',
  'k',
  'l',
  'r',
  't',
  'y',
  'f',
  'g',
  'h',
  '1',
  '2',
]);
const EXIT_CANCEL_KEYS = new Set(['escape', '8', '9']);

type ExitChoice = 'si' | 'no';

function createVirtualGamepads() {
  const gamepads: [VirtualGamepad, VirtualGamepad] = [
    { buttons: Array(17).fill(false) },
    { buttons: Array(17).fill(false) },
  ];

  const handleKey = (e: KeyboardEvent, pressed: boolean) => {
    const key = e.key.toLowerCase();

    if (BACK_KEYS_P1.has(key)) {
      gamepads[0].buttons[7] = pressed;
      return;
    }
    if (BACK_KEYS_P2.has(key)) {
      gamepads[1].buttons[7] = pressed;
    }
  };

  const onKeyDown = (e: KeyboardEvent) => handleKey(e, true);
  const onKeyUp = (e: KeyboardEvent) => handleKey(e, false);

  window.addEventListener('keydown', onKeyDown);
  window.addEventListener('keyup', onKeyUp);

  return {
    getGamepads: () => gamepads,
    cleanup: () => {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup', onKeyUp);
    },
  };
}

interface MachineGameViewProps {
  slug: string;
  title: string;
  challengeId: string | null;
  gameId: string | null;
}

export function MachineGameView({
  slug,
  title,
  challengeId,
  gameId,
}: MachineGameViewProps) {
  const isDevelopmentEnvironment = import.meta.env.DEV;
  const virtualGamepadsRef = useRef<ReturnType<
    typeof createVirtualGamepads
  > | null>(null);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const {
    supported: hidSupported,
    count: hidCount,
    connect: hidConnect,
    snapshotsRef: hidSnapshotsRef,
  } = useProControllers();

  const [exitOpen, setExitOpen] = useState(false);
  const [exitChoice, setExitChoice] = useState<ExitChoice>('no');
  const exitOpenRef = useRef(false);
  const exitChoiceRef = useRef<ExitChoice>('no');

  // Back the embed's platanusArcadeStorage bridge with namespaced localStorage,
  // same as game-player. Without this, storage-using games time out the bridge.
  useArcadeStorageBridge({
    iframeRef,
    iframeOrigin: typeof window !== 'undefined' ? window.location.origin : '',
    challengeId,
    gameId,
  });

  const chooseExit = useCallback((choice: ExitChoice) => {
    exitChoiceRef.current = choice;
    setExitChoice(choice);
  }, []);

  const closeExit = useCallback(() => {
    exitOpenRef.current = false;
    setExitOpen(false);
  }, []);

  const openExit = useCallback(() => {
    exitOpenRef.current = true;
    exitChoiceRef.current = 'no';
    setExitChoice('no');
    setExitOpen(true);
  }, []);

  // Single entry point for the coin/back control: open the modal, or dismiss it
  // if it is already showing.
  const toggleExit = useCallback(() => {
    if (exitOpenRef.current) {
      closeExit();
    } else {
      openExit();
    }
  }, [closeExit, openExit]);

  const confirmExit = useCallback(() => {
    if (exitChoiceRef.current === 'si') {
      router.visit('/26/arcade/machine');
    } else {
      closeExit();
    }
  }, [closeExit]);

  useEffect(() => {
    virtualGamepadsRef.current = createVirtualGamepads();

    const handleMessage = (event: MessageEvent) => {
      if (event.data?.type === 'arcade-back') {
        toggleExit();
      }
    };

    window.addEventListener('message', handleMessage);

    return () => {
      virtualGamepadsRef.current?.cleanup();
      window.removeEventListener('message', handleMessage);
    };
  }, [toggleExit]);

  // Keyboard control of the exit modal (cabinet wired as keyboard).
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (!exitOpenRef.current) {
        return;
      }
      const key = e.key.toLowerCase();
      if (EXIT_LEFT_KEYS.has(key)) {
        e.preventDefault();
        chooseExit('si');
      } else if (EXIT_RIGHT_KEYS.has(key)) {
        e.preventDefault();
        chooseExit('no');
      } else if (EXIT_CONFIRM_KEYS.has(key)) {
        e.preventDefault();
        confirmExit();
      } else if (EXIT_CANCEL_KEYS.has(key)) {
        e.preventDefault();
        closeExit();
      }
    };

    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [chooseExit, confirmExit, closeExit]);

  useEffect(() => {
    let animationId: number;
    const lastButtons: [boolean[], boolean[]] = [
      Array(17).fill(false),
      Array(17).fill(false),
    ];
    const lastAxisLeft: [boolean, boolean] = [false, false];
    const lastAxisRight: [boolean, boolean] = [false, false];
    const DEADZONE = 0.5;

    const pollGamepad = () => {
      const realGamepads = firstConnectedGamepads(readGamepads(navigator));
      const hidPlayers = new Set(hidSnapshotsRef.current.map((s) => s.player));
      const virtualGamepads = virtualGamepadsRef.current?.getGamepads() ?? [];

      for (let playerIndex = 0; playerIndex < 2; playerIndex++) {
        // A WebHID controller can also appear through the browser Gamepad API.
        // The existing decoded HID path owns that player slot.
        const realGp = hidPlayers.has(playerIndex + 1)
          ? undefined
          : realGamepads[playerIndex];
        const virtualGp = virtualGamepads[playerIndex];

        const buttons: boolean[] = Array(17).fill(false);
        let axisX = 0;

        if (realGp) {
          for (let i = 0; i < realGp.buttons.length; i++) {
            // Standard button 7 is the right trigger; Back/Select is 8.
            if (realGp.mapping === 'standard' && (i === 7 || i === 8)) {
              continue;
            }
            buttons[i] = buttons[i] || realGp.buttons[i].pressed;
          }
          if (realGp.mapping === 'standard') {
            buttons[7] = realGp.buttons[8]?.pressed ?? false;
          }
          if (Math.abs(realGp.axes[0] ?? 0) > Math.abs(axisX)) {
            axisX = realGp.axes[0] ?? 0;
          }
        }

        if (virtualGp) {
          for (let i = 0; i < virtualGp.buttons.length; i++) {
            buttons[i] = buttons[i] || virtualGp.buttons[i];
          }
        }

        const wasPressed = (index: number) =>
          buttons[index] && !lastButtons[playerIndex][index];

        const axisLeft = axisX < -DEADZONE;
        const axisRight = axisX > DEADZONE;
        const axisLeftEdge = axisLeft && !lastAxisLeft[playerIndex];
        const axisRightEdge = axisRight && !lastAxisRight[playerIndex];

        // Coin button (index 7) opens/closes the exit modal.
        if (wasPressed(7)) {
          toggleExit();
        }

        if (exitOpenRef.current) {
          if (wasPressed(14) || axisLeftEdge) {
            chooseExit('si');
          }
          if (wasPressed(15) || axisRightEdge) {
            chooseExit('no');
          }
          if (wasPressed(0) || wasPressed(9)) {
            confirmExit();
          }
          if (wasPressed(1)) {
            closeExit();
          }
        }

        lastButtons[playerIndex] = [...buttons];
        lastAxisLeft[playerIndex] = axisLeft;
        lastAxisRight[playerIndex] = axisRight;
      }

      animationId = requestAnimationFrame(pollGamepad);
    };

    animationId = requestAnimationFrame(pollGamepad);
    return () => cancelAnimationFrame(animationId);
  }, [toggleExit, chooseExit, confirmExit, closeExit, hidSnapshotsRef]);

  // Controllers are read in the parent: the production sandbox has an opaque
  // origin. Translate WebHID and standard Gamepad state into the existing bridge
  // (controlCode -> synthetic key). Minus is the back/coin button. While the
  // exit modal is open we stop forwarding gameplay input and drive the modal
  // instead.
  useEffect(() => {
    // Sandboxed iframes without allow-same-origin have an opaque origin, so the
    // post target must be '*' in production (matches the embed's own checks).
    const postTarget = isDevelopmentEnvironment ? window.location.origin : '*';
    const pressed = new Map<ArcadeControlCode, boolean>();
    const lastBack: Record<number, boolean> = {};
    const lastNavLeft: Record<number, boolean> = {};
    const lastNavRight: Record<number, boolean> = {};
    const lastConfirm: Record<number, boolean> = {};
    let animationId: number;

    const post = (controlCode: ArcadeControlCode, isPressed: boolean) => {
      iframeRef.current?.contentWindow?.postMessage(
        { type: 'arcade:virtual-control', controlCode, pressed: isPressed },
        postTarget,
      );
    };

    const releaseAll = () => {
      for (const [code, wasPressed] of pressed) {
        if (wasPressed) {
          post(code, false);
          pressed.set(code, false);
        }
      }
    };

    const poll = () => {
      const next = new Map<ArcadeControlCode, boolean>();

      for (const snapshot of hidSnapshotsRef.current) {
        if (snapshot.player > 2) {
          continue; // only P1/P2 are wired on the cabinet
        }

        const backNow = snapshot.buttons.minus;
        if (backNow && !lastBack[snapshot.player]) {
          toggleExit();
        }
        lastBack[snapshot.player] = backNow;

        if (exitOpenRef.current) {
          // Use the same mapping the menu polls so confirm/navigation respond
          // to exactly the physical buttons (e.g. the cabinet's play button is
          // wired to Y, which menu button 0 also accepts — not just A).
          const menuGp = proControllerToMenuGamepad(snapshot);
          const leftNow = menuGp.buttons[14];
          const rightNow = menuGp.buttons[15];
          const confirmNow = menuGp.buttons[0] || menuGp.buttons[9];

          if (leftNow && !lastNavLeft[snapshot.player]) {
            chooseExit('si');
          }
          if (rightNow && !lastNavRight[snapshot.player]) {
            chooseExit('no');
          }
          if (confirmNow && !lastConfirm[snapshot.player]) {
            confirmExit();
          }

          lastNavLeft[snapshot.player] = leftNow;
          lastNavRight[snapshot.player] = rightNow;
          lastConfirm[snapshot.player] = confirmNow;
          continue; // suppress gameplay forwarding while the modal is open
        }

        const player = snapshot.player === 2 ? 2 : 1;
        const controls = proControllerToArcadeControls(snapshot, player);
        for (const [code, value] of Object.entries(controls) as Array<
          [ArcadeControlCode, boolean]
        >) {
          if (value) {
            next.set(code, true);
          }
        }
      }

      const hidPlayers = new Set(hidSnapshotsRef.current.map((s) => s.player));
      const gamepads = firstConnectedGamepads(readGamepads(navigator));
      if (!exitOpenRef.current) {
        for (const [index, gamepad] of gamepads.entries()) {
          const player = index === 0 ? 1 : 2;
          if (hidPlayers.has(player)) {
            continue; // WebHID owns the slot; never forward both sources.
          }
          const controls = standardGamepadToArcadeControls(gamepad, player);
          for (const [code, value] of Object.entries(controls) as Array<
            [ArcadeControlCode, boolean]
          >) {
            if (value) {
              next.set(code, true);
            }
          }
        }
      } else {
        // A later player's Back may have opened the modal after an earlier
        // player's gameplay was collected in this frame. Release every player.
        next.clear();
      }

      // Emit press/release diffs against the previous frame. Missing, denied,
      // unsupported and disconnected Gamepads produce releases automatically.
      for (const [code, wasPressed] of pressed) {
        if (wasPressed && !next.get(code)) {
          post(code, false);
          pressed.set(code, false);
        }
      }
      for (const code of next.keys()) {
        if (!pressed.get(code)) {
          post(code, true);
          pressed.set(code, true);
        }
      }

      animationId = requestAnimationFrame(poll);
    };

    // The iframe may finish loading after a held press was first posted.
    // Replay current controls on the next frame once its receiver is ready.
    const iframe = iframeRef.current;
    const replayOnLoad = () => pressed.clear();
    iframe?.addEventListener('load', replayOnLoad);
    animationId = requestAnimationFrame(poll);
    return () => {
      cancelAnimationFrame(animationId);
      iframe?.removeEventListener('load', replayOnLoad);
      releaseAll();
    };
  }, [
    toggleExit,
    chooseExit,
    confirmExit,
    hidSnapshotsRef,
    isDevelopmentEnvironment,
  ]);

  return (
    <>
      {hidSupported && hidCount < 2 && (
        <ConnectControllerButton onConnect={hidConnect} />
      )}
      <div className="theme-ph26 flex h-screen w-screen items-center justify-center overflow-hidden bg-black">
        {/* Game container — 4:3, full height; border hugs the actual game */}
        <div
          className="relative aspect-[4/3] h-full max-h-screen overflow-hidden bg-black"
          style={{
            boxShadow:
              'inset 0 0 0 4px hsl(var(--primary)), 0 0 60px rgba(0, 0, 0, 0.5)',
          }}
        >
          <iframe
            ref={iframeRef}
            src={`/26/arcade/${slug}/embed`}
            title="Arcade Game"
            className="h-full w-full"
            allow="autoplay"
            sandbox={
              isDevelopmentEnvironment
                ? 'allow-scripts allow-same-origin'
                : 'allow-scripts'
            }
            loading="eager"
          />

          {exitOpen && (
            <div className="absolute inset-0 z-20 flex items-center justify-center bg-black/80 backdrop-blur-sm">
              <div className="mx-6 w-full max-w-md border-4 border-primary bg-background p-8 text-center shadow-[0_0_40px_rgba(0,0,0,0.6)]">
                <h2 className="font-bold text-2xl text-primary">
                  ¿Quieres salir de{' '}
                  <span className="text-foreground">{title}</span>?
                </h2>
                <div className="mt-8 flex items-center justify-center gap-4">
                  <button
                    type="button"
                    onClick={() => {
                      chooseExit('si');
                      router.visit('/26/arcade/machine');
                    }}
                    onMouseEnter={() => chooseExit('si')}
                    className={`min-w-28 border-2 px-6 py-3 font-bold font-mono text-lg uppercase transition-all ${
                      exitChoice === 'si'
                        ? 'animate-pulse-glow border-primary bg-primary text-background'
                        : 'border-primary/40 bg-background text-primary hover:border-primary/70'
                    }`}
                  >
                    Sí
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      chooseExit('no');
                      closeExit();
                    }}
                    onMouseEnter={() => chooseExit('no')}
                    className={`min-w-28 border-2 px-6 py-3 font-bold font-mono text-lg uppercase transition-all ${
                      exitChoice === 'no'
                        ? 'animate-pulse-glow border-primary bg-primary text-background'
                        : 'border-primary/40 bg-background text-primary hover:border-primary/70'
                    }`}
                  >
                    No
                  </button>
                </div>
                <p className="mt-6 font-mono text-primary/50 text-xs">
                  ← → para elegir · A/START confirma
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
